"use client";

import { useMemo, useState } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconMinus,
  IconPlus,
  IconSearch,
  IconX,
} from "@/lib/icons";
import SafeHtml from "../CMS/SafeHtml";
import PageSchema from "@/components/SEO/PageSchema";
import { buildFaqPageSchema } from "@/lib/faqSchema";
import styles from "./eventFaq.module.css";

const INITIAL_VISIBLE_COUNT = 5;

const INITIALLY_OPEN_COUNT = 0;

function extractFaqItems(data) {
  if (Array.isArray(data?.faqs)) return data.faqs;
  if (Array.isArray(data)) return data;
  return (
    Object.values(data || {}).find(
      (value) => Array.isArray(value) && value.some((item) => item?.question),
    ) || []
  );
}

export default function EventFaq({ data, isThirdParty = false }) {
  const allFaqs = useMemo(() => {
    const rawItems = extractFaqItems(data).filter(
      (faq) => faq?.question && faq?.answer,
    );
    if (isThirdParty) {
      return rawItems.filter((faq) => {
        const q = String(faq?.question || "");
        const a = String(faq?.answer || "");
        return !/{{\s*(platform|company_name)/i.test(`${q} ${a}`);
      });
    }
    return rawItems;
  }, [data, isThirdParty]);

  // First answer open by default — see FAQSection.js.
  const [openItems, setOpenItems] = useState(
    () => new Set(Array.from({ length: INITIALLY_OPEN_COUNT }, (_, i) => i)),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter FAQs based on search input
  const filteredFaqs = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) return allFaqs;
    return allFaqs.filter((faq) => {
      const q = (faq.question || "").toLowerCase();
      const a = (faq.answer || "").toLowerCase();
      return q.includes(trimmed) || a.includes(trimmed);
    });
  }, [allFaqs, searchTerm]);

  // Derived from the full unfiltered list — the schema describes the page, not
  // whatever the visitor has typed into the search box.
  const faqSchema = useMemo(() => buildFaqPageSchema(allFaqs), [allFaqs]);

  if (allFaqs.length === 0) return null;

  const title = data?.title || "Frequently Asked Questions";
  const isSearching = searchTerm.trim().length > 0;

  // Every match renders; items past the initial count are collapsed with CSS
  // rather than sliced out, so they stay in the server-rendered HTML. Search
  // is client-only, so the SSR pass always renders the complete list.
  const hasMore = !isSearching && filteredFaqs.length > INITIAL_VISIBLE_COUNT;

  const toggleItem = (index) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <section className={styles.eventFaq} aria-label="Event FAQs">
      <PageSchema schemas={[faqSchema]} idPrefix="event-faq-schema" />
      {/* Header with Title & Badge */}
      <div className={styles.headerSection}>
        <div className={styles.titleRow}>
          <div className={styles.titleWrap}>
            <h2 className={styles.sectionTitle}>{title}</h2>
          </div>
          <span className={styles.countBadge}>
            {allFaqs.length} {allFaqs.length === 1 ? "question" : "questions"}
          </span>
        </div>
      </div>

      {/* FAQs 2-Column Grid */}
      {filteredFaqs.length > 0 ? (
        <div className={styles.faqGrid}>
          {filteredFaqs.map((faq, index) => {
            const isOpen = openItems.has(index);
            const isBeyondInitial =
              !isSearching && !isExpanded && index >= INITIAL_VISIBLE_COUNT;
            const panelId = `event-faq-answer-${index}`;
            return (
              <div
                key={index}
                className={`${styles.faqCard} ${isOpen ? styles.faqCardActive : ""}`}
                hidden={isBeyondInitial}
              >
                <button
                  type="button"
                  className={styles.faqHeader}
                  onClick={() => toggleItem(index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className={styles.question}>{faq.question}</span>
                  <span className={styles.expandIcon}>
                    {isOpen ? <IconMinus size={15} /> : <IconPlus size={15} />}
                  </span>
                </button>
                {/* Always rendered, hidden when closed — see FAQSection.js */}
                <div id={panelId} className={styles.faqBody} hidden={!isOpen}>
                  <SafeHtml html={faq.answer} className={styles.answer} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State for Search */
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>
            No questions found matching &ldquo;{searchTerm}&rdquo;
          </p>
          <button
            type="button"
            className={styles.resetButton}
            onClick={handleClearSearch}
          >
            Show all questions
          </button>
        </div>
      )}

      {/* "View All" / "Show Less" Toggle Button */}
      {hasMore && (
        <div className={styles.toggleWrapper}>
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() => {
              setIsExpanded((prev) => !prev);
              setOpenItems(new Set());
            }}
            aria-expanded={isExpanded}
          >
            <span>
              {isExpanded
                ? "Show less FAQs"
                : `View all ${allFaqs.length} FAQs`}
            </span>
            {isExpanded ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </button>
        </div>
      )}
    </section>
  );
}
